import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import config from "./config";
import { DEFAULT_BRAND, FloatingField, ICON } from "./MembershipLogin";
import { brandBackgroundStyle } from "./lib/brandBackground";
import { paletteForMode } from "./brandColor";
import { paintedMode } from "./theme";

/**
 * Reset-password page — `/resetpassword?id=<encrypted funnel_id>&token=<token>`.
 *
 * This is the target of the link emailed by api/forgot_password. It collects a
 * new password and posts to api/reset_password with the encrypted funnel id
 * (`id`) and reset `token` straight from the URL. On success the API returns the
 * community login slug (login_token) so we redirect to that membership's login.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id"); // encrypted funnel_id (Crypt)
  const token = searchParams.get("token"); // reset token

  const [form, setForm] = useState({ password: "", confirmpassword: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const brand = DEFAULT_BRAND;
  // Same mode correction as the login page — DEFAULT_BRAND's primary is the
  // near-black #012329, which disappears on the dark canvas.
  const resetPalette = paletteForMode(brand, paintedMode());
  const theme = resetPalette.primary;
  const btnTxt = resetPalette.btnText;
  // Same image/solid/gradient contract as the login page. This page has no
  // funnel code to resolve branding with, so it stays on DEFAULT_BRAND — but it
  // must not be the one surface that ignores bgmode if that ever changes.
  const bgStyle = useMemo(() => brandBackgroundStyle(brand), [brand]);

  const invalidLink = !id || !token;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      return setMsg({ type: "error", text: "Password must be at least 8 characters." });
    }
    if (form.password !== form.confirmpassword) {
      return setMsg({ type: "error", text: "Passwords don't match." });
    }
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.API_URL}/reset_password`, {
        token,
        funnel_id: id, // the API Crypt::decrypt()s this
        password: form.password,
        confirmpassword: form.confirmpassword,
      });
      if (res.data?.success) {
        setDone(true);
        toast.success("Your password has been reset.");
        const loginToken = res.data.login_token;
        if (loginToken) {
          setTimeout(() => navigate(`/membership/${loginToken}/login`, { replace: true }), 1500);
        }
      } else {
        setMsg({ type: "error", text: res.data?.message || "This reset link is invalid or has expired." });
      }
    } catch (err) {
      // Laravel validation errors come back as { errors: { field: [msg] } }.
      const errs = err.response?.data?.errors;
      const first = errs && Object.values(errs)[0]?.[0];
      setMsg({ type: "error", text: first || err.response?.data?.message || "Couldn't reset your password. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 relative" style={bgStyle}>
      <div className="absolute inset-0" style={{ background: brand.overlaycolor }} aria-hidden="true" />

      <div className="relative w-full max-w-[380px] bg-surface rounded-lg shadow-2xl px-7 py-9">
        <div className="flex flex-col items-center gap-5">
          <img src={brand.logo} alt="Logo" className="h-[60px] w-auto object-contain" />

          <p className="text-lg font-semibold text-black dark:text-gray-900 text-center leading-snug">
            {done ? (
              <>Password <br /> updated</>
            ) : (
              <>Set a new <br /> password</>
            )}
          </p>

          {msg.type === "error" && msg.text && (
            <div className="w-full text-sm rounded-md px-3 py-2 bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-300 border border-red-100 dark:border-red-500/30">{msg.text}</div>
          )}

          {invalidLink ? (
            <p className="text-sm text-gray-500 text-center">
              This reset link is invalid or incomplete. Please request a new one from the login page.
            </p>
          ) : done ? (
            <div className="w-full flex flex-col items-center gap-4 text-center">
              <p className="text-sm text-gray-500">Your password has been reset. Redirecting you to login…</p>
            </div>
          ) : (
            <form className="w-full flex flex-col gap-4" onSubmit={submit}>
              <FloatingField
                label="New password"
                type={show ? "text" : "password"}
                value={form.password}
                onChange={set("password")}
                theme={theme}
                disabled={busy}
                required
                icon={show ? ICON.eyeOff : ICON.eye}
                onIconClick={() => setShow((s) => !s)}
              />
              <FloatingField
                label="Confirm new password"
                type={show ? "text" : "password"}
                value={form.confirmpassword}
                onChange={set("confirmpassword")}
                theme={theme}
                disabled={busy}
                required
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full text-center text-lg rounded font-medium py-2.5 transition hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: theme, color: btnTxt }}
              >
                {busy ? "Please wait…" : "Reset password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
